/**
 * Entities Store Mutations
 * Type-safe mutations for normalized entities state
 */

import {
  EntitiesState,
  HomeEntity,
  RoomEntity,
  DeviceEntity,
  GatewayEntity,
  Relationships,
  NormalizedData,
  LastTelemetryData,
  createEmptyRelationships,
} from './types';

export default {
  /**
   * Set entire normalized data structure
   * Used for cache restoration
   */
  setNormalizedData(
    state: EntitiesState,
    payload: Partial<NormalizedData> & { photoUrlByHome?: Record<string, string> }
  ): void {
    state.homes = payload.homes || {};
    state.rooms = payload.rooms || {};
    state.devices = payload.devices || {};
    state.gateways = payload.gateways || {};
    state.relationships = payload.relationships || createEmptyRelationships();
    state.photoUrlByHome = payload.photoUrlByHome || {};
    state.lastFetched = Date.now();
  },

  /**
   * Replace homes/rooms/devices/gateways/relationships wholesale with a fresh
   * full snapshot from the backend (e.g. after loadAllData completes).
   * Unlike the merge* mutations, this drops any entity that no longer exists
   * on the backend (deleted homes/rooms/devices/gateways), preventing stale
   * "ghost" entities from surviving a refresh.
   */
  replaceEntities(state: EntitiesState, payload: NormalizedData): void {
    state.homes = payload.homes;
    state.rooms = payload.rooms;
    state.devices = payload.devices;
    state.gateways = payload.gateways;
    state.relationships = payload.relationships;
    state.lastFetched = Date.now();
  },

  /**
   * Merge homes data into state
   * Uses spread operator for immutability
   */
  mergeHomes(state: EntitiesState, homes: Record<string, HomeEntity>): void {
    state.homes = { ...state.homes, ...homes };
  },

  /**
   * Merge rooms data into state
   */
  mergeRooms(state: EntitiesState, rooms: Record<string, RoomEntity>): void {
    state.rooms = { ...state.rooms, ...rooms };
  },

  /**
   * Merge devices data into state
   */
  mergeDevices(state: EntitiesState, devices: Record<string, DeviceEntity>): void {
    state.devices = { ...state.devices, ...devices };
  },

  /**
   * Merge gateways data into state
   */
  mergeGateways(state: EntitiesState, gateways: Record<string, GatewayEntity>): void {
    state.gateways = { ...state.gateways, ...gateways };
  },

  /**
   * Update relationships by merging with existing
   * Handles partial relationship updates
   */
  updateRelationships(state: EntitiesState, relationships: Partial<Relationships>): void {
    state.relationships = {
      deviceToGateway: {
        ...state.relationships.deviceToGateway,
        ...(relationships.deviceToGateway || {}),
      },
      deviceToRoom: {
        ...state.relationships.deviceToRoom,
        ...(relationships.deviceToRoom || {}),
      },
      deviceToHome: {
        ...state.relationships.deviceToHome,
        ...(relationships.deviceToHome || {}),
      },
      gatewayToHome: {
        ...state.relationships.gatewayToHome,
        ...(relationships.gatewayToHome || {}),
      },
      roomToHome: {
        ...state.relationships.roomToHome,
        ...(relationships.roomToHome || {}),
      },
      homeToRooms: {
        ...state.relationships.homeToRooms,
        ...(relationships.homeToRooms || {}),
      },
      homeToDevices: {
        ...state.relationships.homeToDevices,
        ...(relationships.homeToDevices || {}),
      },
      homeToGateways: {
        ...state.relationships.homeToGateways,
        ...(relationships.homeToGateways || {}),
      },
      roomToDevices: {
        ...state.relationships.roomToDevices,
        ...(relationships.roomToDevices || {}),
      },
      gatewayToDevices: {
        ...state.relationships.gatewayToDevices,
        ...(relationships.gatewayToDevices || {}),
      },
    };
  },

  /**
   * Update device telemetry (for real-time WebSocket updates)
   */
  updateDeviceTelemetry(
    state: EntitiesState,
    payload: { deviceId: string; lastTelemetry: LastTelemetryData }
  ): void {
    state.deviceTelemetry = {
      ...state.deviceTelemetry,
      [payload.deviceId]: {
        ...payload.lastTelemetry,
        timestamp: Number(payload.lastTelemetry.timestamp)
      },
    };
  },

  /**
   * Set photo URL for a specific home
   */
  setPhotoUrlForHome(
    state: EntitiesState,
    payload: { homeUid: string; photoUrl: string }
  ): void {
    state.photoUrlByHome = {
      ...state.photoUrlByHome,
      [payload.homeUid]: payload.photoUrl,
    };
  },

  /**
   * Update cache timestamp to current time
   */
  updateCacheTimestamp(state: EntitiesState): void {
    state.lastFetched = Date.now();
  },

  /**
   * Clear all cached data and reset to initial state
   */
  clearCache(state: EntitiesState): void {
    state.homes = {};
    state.rooms = {};
    state.devices = {};
    state.gateways = {};
    state.relationships = createEmptyRelationships();
    state.deviceTelemetry = {};
    state.photoUrlByHome = {};
    state.lastFetched = null;
  },
};
